// @ts-strict-ignore
import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  Output,
  OnDestroy,
  QueryList,
  ViewChild,
  ViewChildren,
  Renderer2,
  NgZone,
  SimpleChanges,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
  SohoApplicationMenuComponent,
  SohoBusyIndicatorDirective,
  SohoButtonComponent,
  SohoMenuButtonComponent,
  SohoToolbarFlexComponent,
  SohoToolbarFlexMoreButtonComponent
} from 'ids-enterprise-ng';
import { AppConstants } from '@utils/app.constants';
import { LMLoggerService } from '@services/lm-logger.service';
import { LMContextService } from '@services/lm-context.service';
import { LMBusyIndicatorService } from '@services/lm-busyindicator.service';
import { SubscriptionLike as ISubscription, filter } from 'rxjs';
import { isEmpty } from 'lodash-es';
import { LMConfigurationService } from '@services/lm-configuration.service';
import { LMFormatUtilService } from '@services/utils/lm-format-util.service';
import { LMBrowserUtilService } from '@utils/lm-browser-util.service';
import { LMContextUtilService } from '@app/utils';
import { LMToolbarMoreButtonMenuParams, LMToolbarServiceBase } from '../toolbar/lm-toolbar-service.base';
import { LMGlobalStateService } from '@states/services/lm-global-state.service';
import { LMGlobalPersonalizationService } from '@app/states/services/lm-global-personalization.service';
import { LMOverlayService } from '@app/services';
import { LMXLFileUploadService } from '@services/file-upload/lm-xlfile-upload.service';
import { LiveRegionConfig } from 'directives/lm-live-region.directive';
import { LMGlobalState } from "@app/states";

@Component({
  selector: 'lm-toolbar',
  standalone: false,
  providers: [ LMToolbarServiceBase ],
  templateUrl: './lm-toolbar.component.html',
  styleUrls: [ 'lm-toolbar.component.scss' ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  })
export class LMToolbarComponent implements AfterViewInit, AfterViewChecked, OnChanges, OnDestroy {
  @HostBinding('style.display') public isBlock = 'block';
  @HostBinding('class.isMobileDevice') isMobileDevice = false;
  @HostBinding('id') public toolbarId: string;

  @HostBinding('attr.data-automation-id') get dataAutomationId() {
    return `lm-${this.automationId}-toolbar`;
  }

  @HostBinding('class.has-result-set') get hasResultSet() {
    return this.showMustache;
  }

  @HostListener('window:resize') onResize() {
    this.setMaxTitleWidth();

    if (window.innerWidth < Soho.breakpoints.slim) {
      this.maxVisibleButtons = 2;
    }
  }

  // @HostListener('click', [ '$event' ])
  // onClick(event: PointerEvent) {
  //   if (this.lmContextService.useListFormOverlay() && this.lmOverlayService.hasOpenOverlay && !this.overlayContent) {
  //     if ((event.target as HTMLElement).nodeName !== 'BUTTON')
  //     this.lmOverlayService.close();
  //   }
  // }

  @ViewChild(SohoToolbarFlexComponent) sohoFlexToolbar: SohoToolbarFlexComponent;
  @ViewChild(SohoToolbarFlexMoreButtonComponent, { read: ElementRef }) sohoToolbarFlexButtonSet: ElementRef;
  @ViewChild(SohoBusyIndicatorDirective) sohoBusyIndicator: SohoBusyIndicatorDirective;
  @ViewChildren('relatedMenu') relatedActionMenus: QueryList<SohoMenuButtonComponent>;
  @ViewChildren(SohoButtonComponent, { read: ElementRef }) htmlButtons: QueryList<ElementRef>;
  @ViewChildren(SohoMenuButtonComponent, { read: ElementRef }) htmlMenuButtons: QueryList<ElementRef>;
  @ViewChild('SohoApplicationMenu') private sohoApplicationMenuComponent: SohoApplicationMenuComponent | undefined;
  @ViewChild('menuTrigger', { read: ElementRef, static: false }) menuTrigger: ElementRef;
  @ViewChild('pageTitle', { read: ElementRef, static: false }) pageTitle: ElementRef;

  @Input() public set automationId(automationId: string) {
    this.m_automationId = automationId;
    this.toolbarAttributes = [ { name: 'data-automation-id', value: `lm-${this.automationId}` } ];
  }
  public get automationId(): string {
    return this.m_automationId;
  }

  @Input() set toolbarIdPrefix(toolbarIdPrefix: string) {
    /**
     * for some reason _toolbar_toolbar got placed on teh the toolbar id in version1.
     * todo: ppatton remove this once v1 is no longer supported
     */

    this.toolbarId = toolbarIdPrefix + (this.testVersion1 ? '_toolbar_toolbar' : '_toolbar');

    this.toolbarTitleId = this.inHeader && this.testVersion1 ? 'page-title' : this.toolbarId + '_title';

    /**
     *  MoreButtonId is only valid and set for testVersion2.
     *  In v1 and Id is set on the element above it.
     */
    this.toolbarMoreButtonId = this.testVersion1 ? '' : toolbarIdPrefix + '_toolbar_morebutton';
  }

  // // @Input() public toolbarAutomationIdPrefix;
  // public _toolbarAutomationIdPrefix: string;
  // @Input() set toolbarAutomationIdPrefix(id: string) {
  //   this._toolbarAutomationIdPrefix = id;
  // }
  // get toolbarAutomationIdPrefix(): string {
  //   return this._toolbarAutomationIdPrefix;
  // }

  // ----------------------------------------------------------------------
  // inHeader input
  // ----------------------------------------------------------------------
  @Input()
  set inHeader(inHeader: boolean) {
    this.m_inHeader = inHeader;
  }
  get inHeader(): boolean {
    return this.m_inHeader;
  }

  @Input()
  set asOfDate(asOfDate: string) {
    if (asOfDate) {
      this._asOfDate = asOfDate;

      this.asOfDateMode = true;
      const dateDisplay = this.lmFormatUtilService.yyyymmddToDisplay(this._asOfDate);

      this.asOfDateTitle = Soho.Locale.translate('CreateReportAsOfDateLabel') + ': ' + dateDisplay;
      this.clearAsOfDateTooltip = Soho.Locale.translate('ClearAsOfDateLabel');
    } else {
      this._asOfDate = asOfDate;
      this.asOfDateMode = false;
      this.asOfDateTitle = '';
      this.clearAsOfDateTooltip = '';
    }
  }

  @Input()
  set showArchive(showArchive: boolean) {
    this.viewArchiveMode = !!showArchive;
    this.viewArchiveTitle = Soho.Locale.translate('ArchiveMode');
  }

  @Input()
  set uploadInProgress(uploadInProgress: boolean) {
    this.xlFileUploadInProgress = uploadInProgress;
  }
  // ----------------------------------------------------------------------
  // searchField input
  // ----------------------------------------------------------------------
  @Input()
  set searchField(searchField: LMToolbarSearchField) {
    if (this.m_searchField !== searchField) {
      this.m_searchField = searchField;
      if (this.m_searchField?.scannable) {
        this.setScannableClickEvent();
      }
      this.markForCheck();
    }
  }
  get searchField(): LMToolbarSearchField {
    return this.m_searchField;
  }

  // ----------------------------------------------------------------------
  // sectionTitle input
  // todo name to pageTitle to it matches the class needed in the html.
  // ----------------------------------------------------------------------
  @Input() set sectionTitle(sectionTitle: string) {
    if (this.m_sectionTitle !== sectionTitle) {
      this.m_sectionTitle = sectionTitle;
      this.updateMaxTitleWidth();
      this.markForCheck();
    }
  }
  get sectionTitle(): string {
    return this.m_sectionTitle?.trim();
  }

  @Input() set mustacheText(recordSetSize: string) {
    if (recordSetSize) {
      if (!this.m_mustacheText || (this.m_mustacheText !== recordSetSize)) {
        this.m_mustacheText = recordSetSize;
        this.showMustache = true;
        this.markForCheck();
      } else {
        this.showMustache = false;
      }
    } else {
      this.showMustache = false;
      this.m_mustacheText = undefined;
    }
  }
  get mustacheText(): string {
    return this.m_mustacheText;
  }

  @Input() set isReportAsOfDate(isReportAsOfDate: boolean) {
    this.m_isReportAsOfDate = isReportAsOfDate;
  }

  get isReportAsOfDate(): boolean {
    return this.m_isReportAsOfDate;
  }

  //  todo vonnyw no longer used, not sure what the asofdate set stuff is in here.
  //   does this need to be set somewhere else?
  // // ----------------------------------------------------------------------
  // // pageTitle input
  // // ----------------------------------------------------------------------
  // @Input() set pageTitle(pageTitle: string) {
  //   if (this.m_pageTitle !== pageTitle) {
  //     this.m_pageTitle = pageTitle;
  //
  //     if (this._asOfDate) {
  //       const dateDisplay = this.lmFormatUtilService.yyyymmddToDisplay(this._asOfDate);
  //       this.asOfDateTitle = Soho.Locale.translate('CreateReportAsOfDateLabel') + ': ' + dateDisplay;
  //       this.clearAsOfDateTooltip = Soho.Locale.translate('ClearAsOfDateLabel');
  //     }
  //
  //     this.updateMaxTitleWidth();
  //     this.markForCheck();
  //   }
  // }
  // get pageTitle(): string {
  //   return this.m_pageTitle;
  // }

  // ----------------------------------------------------------------------
  // maxVisibleButtons input
  // ----------------------------------------------------------------------
  @Input()
  set maxVisibleButtons(maxVisibleButtons: number) {
    if (this.m_maxVisibleButtons !== maxVisibleButtons) {
      this.m_maxVisibleButtons = maxVisibleButtons + (this.m_searchField ? 1 : 0);
      this.markForCheck();
    }
  }
  get maxVisibleButtons(): number {
    if (this.m_maxVisibleButtons !== null && this.m_maxVisibleButtons !== undefined) {
      return this.m_maxVisibleButtons;
    }

    return 1; // for more button
  }

  // ----------------------------------------------------------------------
  // hideOverflowMenuButton input
  // ----------------------------------------------------------------------
  @Input()
  set hideOverflowMenuButton(hide: boolean) {
    if (this.m_hideOverflowMenuButton !== hide) {
      this.m_hideOverflowMenuButton = hide;
      this.markForCheck();
    }
  }
  get hideOverflowMenuButton(): boolean {
    return this.m_hideOverflowMenuButton;
  }

  // ----------------------------------------------------------------------
  // showListDetailBackButton input
  // ----------------------------------------------------------------------
  @Input()
  set showListDetailBackButton(val: boolean) {
    this.m_showListDetailBackButton = val;
  }
  get showListDetailBackButton(): boolean {
    return this.m_showListDetailBackButton;
  }

  // ----------------------------------------------------------------------
  // hideToolbarTitles input
  // ----------------------------------------------------------------------
  @Input()
  set hideToolbarTitles(hide: boolean) {
    if (this.m_hideToolbarTitles !== hide) {
      this.m_hideToolbarTitles = hide;
      this.markForCheck();
    }
  }
  get hideToolbarTitles(): boolean {
    return this.m_hideToolbarTitles;
  }

  // ----------------------------------------------------------------------
  // hideToolbarButtons input
  // ----------------------------------------------------------------------
  @Input()
  set hideToolbarButtons(hide: boolean) {
    if (this.m_hideToolbarButtons !== hide) {
      this.m_hideToolbarButtons = hide;
      this.markForCheck();
    }
  }
  get hideToolbarButtons(): boolean {
    return this.m_hideToolbarButtons;
  }

  // ----------------------------------------------------------------------
  // disabled input
  // ----------------------------------------------------------------------
  /**
   * Whether to disable all the buttons in the toolbar or not.
   * @type {boolean}
   */
  @Input()
  set disabled(disabled: boolean) {
    this.m_disabled = disabled;
  }
  get disabled(): boolean {
    return this.m_disabled;
  }

  // ----------------------------------------------------------------------
  // buttons input
  // ----------------------------------------------------------------------
  @Input()
  set buttons(buttons: Array<LMToolbarButton>) {
    if (buttons) {
      buttons.forEach(button => this.resolveButtonTooltip(button));
      this.m_flexButtons = buttons;
      this.m_flexButtonSetButtons = buttons.filter(button => !button.isMoreMenuItem);
      this.visibleButtons = this.m_flexButtonSetButtons.filter(button => button.state.visible);
      this.showButtonSet = this.visibleButtons.length > 0;
      this.m_flexMoreMenuButtons = buttons.filter(button => button.isMoreMenuItem);

      if (!this.hasActiveSubmenus(this.m_flexMoreMenuButtons)) {
        this.m_flexMoreMenuButtons = [];
      }

      this.hideOverflowMenuButton = this.m_flexMoreMenuButtons.length < 1;
      this.doUpdate = true;
    } else {
      this.hideOverflowMenuButton = true;
    }
  }

  get buttons(): Array<LMToolbarButton> {
    return this.m_buttons;
  }

  get flexButtons(): Array<LMToolbarButton> {
    return this.m_flexButtons;
  }

  get flexMoreButtons(): Array<LMToolbarButton> {
    return this.m_flexMoreMenuButtons;
  }

  get flexButtonSetButtons(): Array<LMToolbarButton> {
    return this.m_flexButtonSetButtons;
  }

  goBack = false;
  @Input() set showGoBack(goBack: boolean) {
    this.goBack = goBack;
    this.menuNavigationString = goBack ? Soho.Locale.translate('Back') : Soho.Locale.translate('ToggleNavigation');
    this.markForCheck();
  }

  @Input() moreMenuSettings: SohoPopupMenuOptions;

  @Input() isCreateReportMode: boolean;

  @Input() set isInOverlay(inOv: boolean) {
    this._isInOverlay = inOv;
  }

  get isInOverlay(): boolean {
    return this._isInOverlay;
  }

  @Input() set showOverlayBackButton(sOvB: boolean) {
    this._showOverlayBack = sOvB;
  }

  get showOverlayBackButton(): boolean {
    return this._showOverlayBack;
  }

  @Input() allowOverlayInteractions = false;

  @Input() showCalledOutMenuButton = false;

  @Output() toolbarBackButtonEvent = new EventEmitter();
  @Output() appMenuTriggerEvent = new EventEmitter<MouseEvent>();
  @Output() toolbarButtonEvent = new EventEmitter<LMToolbarButtonEvent>();
  @Output() toolbarSearchEvent = new EventEmitter<LMToolbarSearchEvent>();
  @Output() toolbarSearchClearedEvent = new EventEmitter<LMToolbarSearchEvent>();
  @Output() asOfDateChangedEvent: EventEmitter<string> = new EventEmitter();
  @Output() asOfDateClearedEvent: EventEmitter<void> = new EventEmitter();
  @Output() quitArchiveViewEvent: EventEmitter<void> = new EventEmitter();
  @Output() quitProxyModeEvent: EventEmitter<void> = new EventEmitter();
  @Output() quitDBSessionDebugModeEvent: EventEmitter<void> = new EventEmitter();
  @Output() quitSecSessionDebugModeEvent: EventEmitter<void> = new EventEmitter();
  @Output() quitListCostReportModeEvent: EventEmitter<void> = new EventEmitter();
  @Output() updateReportAsOfDateEvent: EventEmitter<void> = new EventEmitter();

  maximizeTitle = false;

  private m_inHeader: boolean;
  private m_sectionTitle: string;
  private m_pageTitle: string;
  private m_hideOverflowMenuButton = false;
  private m_showListDetailBackButton = false;
  private m_hideToolbarTitles = false;
  private m_hideToolbarButtons = false;
  private m_buttons: Array<LMToolbarButton> = null;
  private m_searchField: LMToolbarSearchField = null;
  private m_disabled = false;
  private m_maxVisibleButtons = null;
  private m_moreMenuButtons: Array<LMToolbarButton>;
  private m_automationId: string;

  private m_mustacheText: string;
  private m_isReportAsOfDate = false;
  public showMustache = false;
  public dbSessionDebugMode = false;
  public secSessionDebugMode = false;
  public listCostReportMode = false;
  public dbSessionDebugId = null;
  public drillAroundWindow = false;
  //public dbSessionTraceString = null;
  public headerTitleLiveConfig: LiveRegionConfig = { useHostAsLiveRegion: false, liveMessage: '', ariaAtomic: false };

  private m_flexButtons: Array<LMToolbarButton> = null;
  private m_flexButtonSetButtons: Array<LMToolbarButton> = null;
  private m_flexMoreMenuButtons: Array<LMToolbarButton> = null;
  private visibleButtons: Array<LMToolbarButton> = null;
  public proxyModeView = false;
  public proxyBannerTitle: string;
  private bannerState: LMGlobalState = {};

  readonly separator: string = AppConstants.CSS_MENU_SEPARATOR;
  public resizeContainers = true;
  public useFlexToolbar = false;
  public disableToolbar = false;
  public toolbarTitleId: string;
  public toolbarMoreButtonId: string;
  public toolbarAttributes: SohoAttributesArray = [];

  public asOfDateTitle: string;
  public asOfDateMode: boolean;
  public clearAsOfDateTooltip: string;
  private _asOfDate: string;

  public viewArchiveMode: boolean;
  public viewArchiveTitle: string;
  /**
   * Whether or not to call updated() on the soho component during
   * the next afterViewChecked lifecycle event.
   */
  public doUpdate = false;

  /**
   * Whether or not to call setMaxTitleWith
   */
  private m_updateMaxTitleWidth = false;

  public JSON = JSON;
  private subscriptionArray: Array<ISubscription> = [];
  private buttonsObserver: MutationObserver;
  public isInAccessibilityMode: boolean;

  public hideNavBar = false;
  private testVersion: LMTestVersion;
  public testVersion1: boolean;
  public menuNavigationString: string;
  public secDebugActiveString: string;
  public costReportActiveString: string;
  public dbDebugLevel: 'basic' | 'minimal' | 'standard' | 'verbose';
  public dbSessionWithTrace: boolean;
  public dbListCostReportModeTooltip  = Soho.Locale.translate('ExitListCostReportMode');
  public dbSessionDebugModeTooltip  = Soho.Locale.translate('ExitDBSessionDebugMode');
  public secSessionDebugModeTooltip  = Soho.Locale.translate('ExitSecurityDebugMode');
  public archiveModeTooltip = Soho.Locale.translate('ExitArchiveMode');
  public proxyModeTooltip  = Soho.Locale.translate('ExitProxyMode');

  public showButtonSet = false;
  public liveMessageSortAction?: LiveRegionConfig;
  public favorTitle = true;
  public xlFileUploadInProgress: boolean;
  public xlFileUploadTitle: string = Soho.Locale.translate('UploadInProgressBanner');

  private _isInOverlay = false;
  private _showOverlayBack = false;
  private overlaySubscriptionExists = false;
  private overlayClickEventUnsubscribe: () => void;

  constructor(
    private changeDetector: ChangeDetectorRef,
    private elementRef: ElementRef,
    private renderer2: Renderer2,
    private lmContextService: LMContextService,
    private lmContextUtilService: LMContextUtilService,
    private ngZone: NgZone,
    private lmLoggerService: LMLoggerService,
    private lmToolbarServiceBase: LMToolbarServiceBase,
    private lmBusyIndicatorService: LMBusyIndicatorService,
    private lmBrowserUtilService: LMBrowserUtilService,
    private lmFormatUtilService: LMFormatUtilService,
    private lmConfigurationService: LMConfigurationService,
    private lmGlobalPersonalizationService: LMGlobalPersonalizationService,
    private lmGlobalStateService: LMGlobalStateService,
    private lmXLFileUploadService: LMXLFileUploadService,
    private lmOverlayService: LMOverlayService,
    private router: Router
  ) {
    // need in the constructor so it's available for @Input() setters.
    this.testVersion = this.lmContextService.testVersion();
    this.testVersion1 = this.testVersion === AppConstants.TESTVERSION_V1;
    this.menuNavigationString = Soho.Locale.translate('ToggleNavigation');
    //this.dbDebugActiveString = Soho.Locale.translate('DBDebugActive');
    this.secDebugActiveString = Soho.Locale.translate('SECDebugActive');
    this.costReportActiveString = Soho.Locale.translate('COSTReportActive');
    //this.dbDebugTraceString = Soho.Locale.translate('DBDebugTrace');

    // Set CSS class in template if mobile
    this.isMobileDevice = this.lmContextService.isMobileDevice();
    // when in mingle and opening drill in a portal tab the bootstrap
    // runs against the plugin so drillAroundWindow is false.
    // When the drill around url/route runs the drillaround component
    // will progrmatically call setDrillWindow.
    //
    this.setDrillAroundWindow(this.lmContextService.drillAroundWindow());
  }

  ngOnChanges(changes: SimpleChanges): void {
    /**
     * This is to resolve web accessibility issue:
     * Scenarios to test:
     * 1. From Overview Page of one web app, go to a different web app that has Overview as a landing page
     * 2. From a list, open a record whose header title varies for each entry > go to next or previous
     * 3. Pages with mustache text
     *
     * It is expected that header is not announced again if the same menu item is clicked repeatedly or if the page is refreshed.
     */

    if (this.inHeader && (changes['toolbarIdPrefix'] || changes['sectionTitle'])) {
      const mustacheText = this.showMustache ? (this.mustacheText ?? '') : '';
      this.headerTitleLiveConfig = { ...this.headerTitleLiveConfig, liveMessage: `${this.sectionTitle} ${mustacheText}` };
    }
  }

  public setDrillAroundWindow(value: boolean) {
    this.drillAroundWindow = value;
  }

  ngAfterViewInit() {
    this.subscriptionArray.push(
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd)).subscribe(() => {
        this.handlePostNavigationFocus();
        })
    );

    this.relatedActionMenus.changes.subscribe(item => {
      this.relatedActionMenus.forEach(relatedActionMenu => relatedActionMenu.ajaxBeforeOpenFunction = this.onBeforeRelatedActionsOpen);
    });
    // Observe for soho-toolbar-button-set childList mutations
    this.buttonsObserver = new MutationObserver(() => this.toolbarButtonsChanges());

    if (this.sohoToolbarFlexButtonSet) {
      this.buttonsObserver.observe(this.sohoToolbarFlexButtonSet.nativeElement, { childList: true });
    }

    this.hideNavBar = this.lmContextService.isHideMenuBar()
        || this.lmContextService.inMingleOverlay()
        || this.lmContextService.drillAroundWindow()
        // Portal provides its own menu button on Unified desktop
        || (this.lmContextService.isUnified() && !this.lmContextService.isUnifiedMobile())
        // Hide app menu when the user clicked an email link to a unified webapp. isUnified is false, isUnifiedWebapp is true, inMingle is false. The developer will still be able to access the menu
        || (this.lmContextService.isUnifiedWebapp() && !this.lmContextService.isUnified() && !this.lmContextService.inMingle() && !this.lmContextService.isDeveloper())
        // No menu button in Mobile Hub for non-Unified web apps
        || (this.lmContextService.insideInforMobileHub() && !this.lmContextService.isUnified());


    this.testVersion = this.lmContextService.testVersion();
    this.dbSessionDebugMode = this.lmContextService.hasDBSessionDebugMode();
    this.secSessionDebugMode = this.lmContextService.isSecSessionDebugMode();
    this.listCostReportMode = this.lmContextService.isListCostReportMode();
    this.dbSessionDebugId = this.lmContextService.getDBSessionDebugId();
    this.dbDebugLevel = this.lmContextService.getDBDebugLevel();
    //this.dbSessionTraceString = this.lmContextService.hasDBSessionTrace() && this.lmContextService.getDBSessionTrace() ? this.dbDebugTraceString : '';
    this.dbSessionWithTrace = this.lmContextService.hasDBSessionTrace() && this.lmContextService.getDBSessionTrace();
    this.sohoFlexToolbar?.updated({ moreMenuSettings : { autoFocus: true } });
    this.isInAccessibilityMode = this.lmGlobalPersonalizationService.getAccessibilityMode();
    if (this.isInAccessibilityMode) {
      this.sohoFlexToolbar?.updated({ allowTabs: true });
    }
    this.setFavorTitle();

    setTimeout(() => this.updateMaxTitleWidth());
  }

  ngAfterViewChecked(): void {
    if (!this.sohoFlexToolbar) {
      return;
    }

    if (this.lmContextService.inPortalTab()) {
      this.dbSessionDebugMode = this.lmContextService.hasDBSessionDebugMode();
      this.secSessionDebugMode = this.lmContextService.isSecSessionDebugMode();
    }

    this.bannerState = this.lmGlobalStateService.getGlobalState();
    if (this.bannerState.proxyMode) {
      this.proxyModeView = this.bannerState.proxyMode;
      this.proxyBannerTitle = this.bannerState.proxyBannerTitle;
    }

    if (this.doUpdate) {
      this.doUpdate = false;
      this.sohoFlexToolbar.updated();

      // ensure that all angular added ng-star-inserted classes are removed
      this.htmlButtons.forEach((htmlButton: ElementRef) => {
        this.renderer2.removeClass(htmlButton.nativeElement, 'ng-star-inserted');
      });
      this.htmlMenuButtons.forEach((htmlButton: ElementRef) => {
        this.renderer2.removeClass(htmlButton.nativeElement, 'ng-star-inserted');
      });
    }

    if (this.m_updateMaxTitleWidth) {
      this.m_updateMaxTitleWidth = false;
      setTimeout(() => this.setMaxTitleWidth());
    }

    if ( !this.overlaySubscriptionExists && !this.isInOverlay && !this.overlaySubscriptionExists && this.allowOverlayInteractions) {
      this.overlaySubscriptionExists = true;
      this.overlayClickEventUnsubscribe = this.renderer2.listen(this.elementRef.nativeElement, 'click', (event: PointerEvent) => {
        // making sure click in the overlay doesn't cause the overlay to close. Move this to a HostListener when
        // overlay code is not controlled by a CSK anymore
        if (this.lmOverlayService.hasOpenOverlay) {
          if ((event.target as HTMLElement).nodeName !== 'BUTTON')
            this.lmOverlayService.close();
        }
      });
    }
  }

  ngOnDestroy() {
    this.subscriptionArray.forEach(subscription => subscription.unsubscribe());
    this.onBeforeRelatedActionsOpen = null;
    this.buttonsObserver.disconnect();

    if (this.lmContextService.useListFormOverlay() && this.isInOverlay) {
      if (this.overlayClickEventUnsubscribe) {
        this.overlayClickEventUnsubscribe();
      }
    }
  }

  public get dbSessionDebugTitle() {
    let dbDebugLevelString = '';

    switch (this.dbDebugLevel) {
      case 'basic':
        dbDebugLevelString = this.dbSessionWithTrace ? Soho.Locale.translate('DBSessionDebugLoggingActiveBasicWithTrace') : Soho.Locale.translate('DBSessionDebugLoggingActiveBasic') ;
        break;
      case 'minimal':
        dbDebugLevelString = this.dbSessionWithTrace ? Soho.Locale.translate('DBSessionDebugLoggingActiveMinimalWithTrace') : Soho.Locale.translate('DBSessionDebugLoggingActiveMinimal') ;
        break;
      case 'verbose':
        dbDebugLevelString = this.dbSessionWithTrace ? Soho.Locale.translate('DBSessionDebugLoggingActiveVerboseWithTrace') : Soho.Locale.translate('DBSessionDebugLoggingActiveVerbose') ;
        break;
      default:
        dbDebugLevelString = this.dbSessionWithTrace ? Soho.Locale.translate('DBSessionDebugLoggingActiveStandardWithTrace') : Soho.Locale.translate('DBSessionDebugLoggingActiveStandard') ;
        break;
    }

    return dbDebugLevelString;
  }

  public forceUpdate() {
    this.doUpdate = false;
    this.markForCheck();
  }

  private handlePostNavigationFocus(): void {
    if (this.lmContextService.isHideTitlebar() || !this.lmGlobalPersonalizationService.getAccessibilityMode()) {
      return;
    }

    if (this.pageTitle) {
      setTimeout(() => { this.pageTitle.nativeElement.focus(); });
    }
  }

  private toolbarButtonsChanges() {
    if (!this.sohoFlexToolbar || !this.doUpdate || !this.sohoToolbarFlexButtonSet) {
      return;
    }

    const buttonSetChildren = this.sohoToolbarFlexButtonSet.nativeElement.children as HTMLElement[];
    const buttonsLength = Array.from(buttonSetChildren).filter(child => child.nodeName === 'BUTTON').length;

    // Check if DOM buttons are already in sync with model
    const visibleButtonsLength = this.flexButtons.filter(button => button.state.visible).length;

    if (buttonsLength !== visibleButtonsLength) {
      return;
    }

    this.sohoFlexToolbar.updated();
    this.doUpdate = false;
  }

  get moreMenuButtons(): Array<LMToolbarButton> {
    return this.m_moreMenuButtons;
  }

  get menuToolTip(): string {
    if (this.goBack) {
      return Soho.Locale.translate('Back');
    } else {
      if (this.lmBrowserUtilService.isMAC) {
        return Soho.Locale.translate('ToggleMenu') + ' - ' + Soho.Locale.translate('CommandShiftK');
      } else {
        return Soho.Locale.translate('ToggleMenu') + ' - ' + Soho.Locale.translate('CtrlShiftK');
      }
    }
  }

  get isMingleInbox(): boolean {
    return this.lmContextService.inMingleInbox();
  }

  // need to still have an empty soho-toolbar-flex-more-button for the case where there are no options/drill around
  // for the toolbar, but there are multiple buttons that could be pushed into the overflow menu from the button set
  // For example:
  //  - If the toolbar is just the refresh button, then we don't need to see the overflow. In this case the flexMoreButtons
  //       is undefined, so need the safety in the check
  //  - On the mingle inbox toolbars, there aren't options/drill around in the more menu. So we need to make sure there
  //       is an empty soho-toolbar-flex-more-button to overflow into
  //  - If we have more than one visible button, but no overflowed more buttons (i.e. options and drill), then we need
  //       an empty soho-toolbar-flex-more-button for those buttons to overflow into when in mobile/responsive
  get useEmptyMoreButton(): boolean {
    return ((this.flexMoreButtons && this.flexMoreButtons.length === 0) && (this.flexButtons && this.flexButtons.length > 1));
  }

  public trackButton(index, button: LMToolbarButton): string {
    if (button.customToolbarButtonInfo) {
      return button.customToolbarButtonInfo.id;
    }

    return button.actionReference ? button.actionReference.id : null;
  }

  public isDefaultButton(button: LMToolbarButton): boolean {
    if (button === undefined) {
      return false;
    }

    if (button.customToolbarButtonInfo) {
      return false;
    }

    const isNotMenu = !button.menu;

    // A foreign-busclass called-out Create (action is BusinessClass.Create, LMWEB-25175) is a
    // related-type button that is a directly-clickable action, not a related-records menu, so it
    // renders as a plain default button.
    const isNotRelated = (button.onPage || this.lmToolbarServiceBase.isRelatedBusClassCreate(button.actionReference))
      ? true
      : button.actionReference.type !== 'related';

      const isVisible = button.state.visible;
    // const isNotALink = !button.isDisplayAsLink;
    // const isNotButtonWithIconUrl = button.iconUrl === undefined;

    return isNotMenu && isNotRelated && isVisible; // && isNotALink && isNotButtonWithIconUrl;
  }

  public displayAsLink(button: LMToolbarButton): boolean {
    if (button === undefined) {
      return false;
    }

    return button.displayAsLink;
  }

  public isRelatedMenuButton(button: LMToolbarButton): boolean {
    if (button === undefined) {
      return false;
    }

    // GenAI Generated code - Start
    // Exclude the foreign-busclass called-out Create shape: it is a plain action, not a
    // related-records menu button. See LMWEB-25175.
    return button.actionReference.type === 'related'
      && button.state.visible
      && !button.onPage
      && !this.isRelatedBusClassCreate(button.actionReference);
    // GenAI Generated code - End
  }

  /**
   * A called-out related action of the form `action is BusinessClass.ActionName` targets a
   * DIFFERENT business class than the host. It is emitted as a related action with a populated
   * relBusClass and a CREATE actionSpec. Such a button is a direct Create action, not a
   * related-records menu. See LMWEB-25175.
   */
  // GenAI Generated code - Start
  private isRelatedBusClassCreate(actionReference: LMBusActionReference): boolean {
    if (!actionReference) {
      return false;
    }

    const targetsAnotherBusClass = !!actionReference.relBusClass;
    const isCreateAction = actionReference.actionSpec?.type === AppConstants.ACTION_TYPE_CREATE;

    return targetsAnotherBusClass && isCreateAction && actionReference.relation === '';
  }
  // GenAI Generated code - End

  public displayRelatedButtonPopupMenu(button: LMToolbarButton): boolean {
    return !button.hideRelatedButtonPopupMenu;
  }

  public isMenuButton(button: LMToolbarButton): boolean {
    if (button === undefined) {
      return false;
    }

    return button.menu && button.state.visible && button.actionReference.id !== AppConstants.STANDARD_TOOLBAR_DRILLOVERLAY;
  }

  public isCustomButton(button: LMToolbarButton): boolean {
    if (button === undefined) {
      return false;
    }

    return !!button.customToolbarButtonInfo;
  }

  public isCustomMenuButton(button: LMToolbarButton): boolean {
    if (button === undefined) {
      return false;
    }

    return !!button.customToolbarButtonInfo.menu;
  }

  public onAppMenuTrigger(event: MouseEvent) {
    if (this.goBack) {
      this.toolbarBackButtonEvent.emit();
      event.stopImmediatePropagation();
    } else {
      this.appMenuTriggerEvent.emit(event);
    }
  }

  public setToolbarDisabled(state: boolean) {
    this.disableToolbar = state;
    this.changeDetector.markForCheck();
  }

  public setHideNavBar(hideNavBar: boolean) {
    this.hideNavBar = hideNavBar;
  }

  public onToolbarButtonEvent(e: SohoToolbarSelectedEvent, isAppMenu = false) {
    if (e.item[0].id === 'toolbar-search-field') {
      // ignore button events coming from search field
      return;
    }

    // use the busy indicator for a short period to stop processing additional clicks
    // on toolbar buttons; template sets the timeToComplete
    this.lmBusyIndicatorService.showBusyIndicator(this.sohoBusyIndicator);

    const element: HTMLElement = e.event.currentTarget as HTMLElement;
    const isSubMenu = element.classList.contains('submenu');
    const noDataSet = isEmpty(e.item[0].dataset);

    // Return if toolbar or submenu
    if (isSubMenu || noDataSet) {
      this.lmLoggerService.warn('Sub menu was clicked or there was no dataset, exiting');
      return;
    }

    let actionReference: LMBusActionReference;
    let actionSpec: LMActionSpec;

    if (e.item[0].dataset.actionReference) {
      actionReference = JSON.parse(e.item[0].dataset.actionReference);
    }

    if (e.item[0].dataset.actionSpec) {
      actionSpec = JSON.parse(e.item[0].dataset.actionSpec);
    }

    if (e.item[0].dataset.customToolbarButtonInfo) {
      const customToolbarButtonInfo = JSON.parse(e.item[0].dataset.customToolbarButtonInfo);
      this.toolbarButtonEvent.emit({
        customToolbarButtonInfo,
        event: e.event
      });
    } else {
      this.toolbarButtonEvent.emit({
        actionSpec,
        actionReference,
        event: e.event
      });
    }
  }

  onFlexItemSelected(e) {
    // use the busy indicator for a short period to stop processing additional clicks
    // on toolbar buttons; template sets the timeToComplete

    let element: HTMLElement;

    if (e.item.overflowed && e.item.actionButtonLink) {
      element = $(e.item.actionButtonLink).data('originalButton');
    } else if (e.item.selectedAnchor) {
      element = e.item.selectedAnchor[0] as HTMLElement;
    } else {
      element = e.item.element as HTMLElement;
    }

    // Overflowed button is a menu button, use the selected anchor
    if (e.item.overflowed && e.item.selectedAnchor && element.classList.contains('btn-menu')) {
      element = e.item.selectedAnchor[0] as HTMLElement;
    }

    if (!element) {
      // clicking the overflow button to close it, just return
      return;
    }

    const isSubMenu = element.classList.contains('submenu');
    const noDataSet = isEmpty(element.dataset);

    // Return if toolbar or submenu
    if (isSubMenu || noDataSet) {
      this.lmLoggerService.warn('Sub menu was clicked or there was no dataset, exiting');
      return;
    }

    let actionReference: LMBusActionReference;
    let actionSpec: LMActionSpec;
    let hasActionForm: boolean;

    if (element.dataset.actionReference) {
      actionReference = JSON.parse(element.dataset.actionReference);
    }

    if (element.dataset.actionSpec) {
      actionSpec = JSON.parse(element.dataset.actionSpec);
    }

    if (element.dataset.hasActionForm) {
      hasActionForm = element.dataset.hasActionForm === 'true';
    }

    if (!actionReference?.skipDelay) {
      this.lmBusyIndicatorService.showBusyIndicator(this.sohoBusyIndicator);
    }

    if (element.dataset.customToolbarButtonInfo) {
      const customToolbarButtonInfo = JSON.parse(element.dataset.customToolbarButtonInfo);
      this.toolbarButtonEvent.emit({
        customToolbarButtonInfo,
        event:                   e.event
      });
    } else {
      this.toolbarButtonEvent.emit({
        actionSpec,
        actionReference,
        hasActionForm,
        event:           e.event
      });
    }
  }

  private buildUserListViewEntry(specId: string, actionRef: LMBusActionReference, disabled = false) {
    if (actionRef.type === this.separator) {
      return `<li class='separator'></li>`;
    }

    const dataAutomationId = 'lm-' + specId + '-' + actionRef.automationId + '-toolbar-menuitem';
    const menuItem = `<a href="#" data-automation-id="${dataAutomationId}" disabled="${disabled}" data-action-reference=\'${JSON.stringify(actionRef)}\'>${actionRef.label}</a>`;
    // Following code does not work. The class is-indented is outside class attribute on its own.
    // let className = '';
    // if (actionRef.isSubmenu) {
    //   className = (actionRef.isChecked) ? 'submenu is-indented' : 'submenu';
    // } else {
    //   className = (actionRef.isChecked) ? 'is-checked' : '';
    // }
    //

    // return actionRef.isSubmenu ?
    //   `<li class=${className}>${menuItem}<ul class="popupmenu"></ul></li>`:
    //   `<li class=${className}>${menuItem}</li>`;

    let className = (actionRef.isChecked) ? 'is-checked' : '';
    className += (disabled) ? ' is-disabled' : '';
    return actionRef.isSubmenu ? ( actionRef.isChecked
      ? `<li class="submenu is-indented">${menuItem}<ul class="popupmenu"></ul></li>`
      : `<li class="submenu">${menuItem}<ul class="popupmenu"></ul></li>` )
      : `<li class=${className}>${menuItem}</li>`;
  }

  private buildUserListViewMenu(specId: string, actionRef: LMBusActionReference, response: AjaxBeforeOpenResponseFunction) {
    this.lmConfigurationService.fetchUserListViews(actionRef.userListViewUrl, actionRef.lk).subscribe((result: LMUserListViewResponse) => {
      // Build menu of saved searches
      const userListViewActionRef = this.lmToolbarServiceBase.getStandardActionMenuItems().userListView;
      let disableSaveOnly = false;
      let menuContent = result.savedViews.reduce((content: string, userListView: LMUserListViewItem, index: number, array: LMUserListViewItem[]) => {
        const actionReference = {
          ...userListViewActionRef,
          label: userListView.name,
          userListViewItem: userListView,
          searchState: JSON.parse(userListView.searchState),
          isChecked: (userListView.userListView === actionRef.appliedUserListView || userListView.subscribedTo === actionRef.appliedUserListView),

        } as LMBusActionReference;

        if (actionReference.isChecked && !userListView.canModify) {
          disableSaveOnly = true; // Disable "save" but not "save as" when selected SS is a subscription they do not own
        }
        actionReference.automationId += index;
        return content + this.buildUserListViewEntry(specId, actionReference, false);
      }, '');

      if (actionRef.isSubmenu) {
        return response(menuContent);
      }

      const idxAppliedUserListView = result.savedViews.findIndex(uv => uv.userListView === actionRef.appliedUserListView);
      if (result.hasMore) {
        menuContent += this.buildUserListViewEntry(specId, {
          ... this.lmToolbarServiceBase.getStandardToolbarActions().userListViewMoreMenu,
          userListViewUrl: actionRef.userListViewUrl,
          appliedUserListView: actionRef.appliedUserListView,
          disableSave: actionRef.disableSave,
          lk: result.lk,
          isSubmenu: true,
          isChecked: idxAppliedUserListView !== -1,
        }, false);
      }

      // Add separator and save buttons
      menuContent += this.buildUserListViewEntry(specId, this.lmToolbarServiceBase.getStandardSeparatorMenuItem(), false);
      menuContent += this.buildUserListViewEntry(specId, this.lmToolbarServiceBase.getStandardActionMenuItems().userListViewSave, (actionRef.disableSave || disableSaveOnly));
      menuContent += this.buildUserListViewEntry(specId, this.lmToolbarServiceBase.getStandardActionMenuItems().userListViewSaveAs, actionRef.disableSave);
      menuContent += this.buildUserListViewEntry(specId, this.lmToolbarServiceBase.getStandardSeparatorMenuItem(), false);
      menuContent += this.buildUserListViewEntry(specId, this.lmToolbarServiceBase.getStandardActionMenuItems().userListViewClear, actionRef.appliedUserListView === undefined);
      menuContent += this.buildUserListViewEntry(specId, this.lmToolbarServiceBase.getStandardActionMenuItems().userListViewManage, result.savedViews?.length === 0);
      menuContent += this.buildUserListViewEntry(specId, this.lmToolbarServiceBase.getStandardActionMenuItems().userListViewLibrary, false);

      response(menuContent);
    });
  }

  private buildDrillAroundMenu(response: AjaxBeforeOpenResponseFunction, resourceId: string) {
    const moreParams: LMToolbarMoreButtonMenuParams = {
      isContextMenu: false,
      showDrillAround: true,
      drillParams: {
        dataView: {
          resourceId
        }
      }
    };
    this.lmToolbarServiceBase.buildDrillAroundMenu(moreParams, null, response);
  }

  /**
   * This method is called when you load an async toolbar menu outside of the more button menu
   * @param response Callback function to return the html string of menu elements
   * @param options any type for now as defined in ep-ng, to be updated when that changes. Right now it is of type:
   *   {
   *     element: jQuery element of the source toolbar menu button that is triggered to open
   *     contextElement?: jQuery element of the ul for the submenu (only available when isSubmenu=true)
   *     isSubmenu?: boolean
   *   }
   *
   * @returns
   */
  public onBeforeRelatedActionsOpen = (response: AjaxBeforeOpenResponseFunction, options: any) => {
    // This must always be defined
    if (!options?.element) {
      return;
    }

    const src = options.element[0] as HTMLElement;
    const srcId = src.id; // copy to var so the  html elements id doesn't get changed when rdoing a replace on src.id.
    const id = this.testVersion === 1 ? srcId : srcId.replace('_toolbar_button', '');
    const relatedMenuButton: LMToolbarButton = this.flexButtons.find(button => button.id === id);

    if (src?.dataset) {
      // coming into the method from clicking a toolbar button with a menu (that's not in the overflow menu)
      let actionRef = JSON.parse(src.dataset.actionReference) as LMBusActionReference;

      // UserListView Menu & UserListView More Submenu
      if (AppConstants.STANDARD_TOOLBAR_USERLISTVIEW_MENU === actionRef.id) {
        if (options.isSubmenu) {
          let submenuElem = options.contextElement[0].previousSibling as HTMLElement;
          if (!submenuElem) {
            submenuElem = options.contextElement[0].parentElement.previousSibling;
          }
          actionRef = JSON.parse(submenuElem.dataset.actionReference) as LMBusActionReference;
        }
        return this.buildUserListViewMenu(srcId.substr(0, srcId.indexOf('_')), actionRef, response);
      }

      if (AppConstants.STANDARD_TOOLBAR_DRILLOVERLAY === actionRef.id) {
        return this.buildDrillAroundMenu(response, actionRef.drillResourceId);
      }

      if (actionRef.resolvedRelActionUrl) {
        const relActions = this.lmToolbarServiceBase.getRelatedActionMenuItems(actionRef.resolvedRelActionUrl)
          .subscribe((relMenuItemResponse: RelatedMenuItemResponse) => {
            // let i = 0;
            let content = '';
            const actionSpec: LMActionSpec = relMenuItemResponse.actionSpec;
            const actionSpecString = JSON.stringify(actionSpec);
            if (relMenuItemResponse.relatedMenuItems.length === 0) {
              response(`<li><a href="#">${Soho.Locale.translate('NoItemsAvailable')}</a></li>`);
            } else if (relMenuItemResponse.relatedMenuItems.length === 1) {
              relatedMenuButton.hideRelatedButtonPopupMenu = true;
              this.toolbarButtonEvent.emit({
                event: undefined,
                actionReference : {
                  id: actionSpec.name,
                  type: 'related',
                  index: actionSpec.index,
                  label: actionSpec.label,
                  requestAction: actionSpec.requestAction,
                  resourceId: relMenuItemResponse.relatedMenuItems[0].resourceId
                },
                actionSpec,
                hasActionForm: relMenuItemResponse.hasActionForm
              });
            } else {
              relatedMenuButton.hideRelatedButtonPopupMenu = false;
              relMenuItemResponse.relatedMenuItems.forEach((item, index) => {
                // Build Action Reference
                const menuElementId = JSON.stringify(item.resourceId);
                const actionReference: LMBusActionReference = {
                  id: actionSpec.name + '_popup' + index,
                  type: 'related',
                  index: actionSpec.index,
                  label: actionSpec.label,
                  requestAction: actionSpec.requestAction,
                  resourceId: item.resourceId
                };
                const actionReferenceString = JSON.stringify(actionReference);
                content += this.buildToolbarMenuEntry(actionReferenceString, actionSpecString, item.text, relMenuItemResponse.hasActionForm);
              });
              response(content);
            }
            this.subscriptionArray.push(relActions);
          });
      }
    } else {
      const request = <any> event.srcElement;
      // coming into the method from hovering over a toolbar more menu entry with a menu
      const relatedActionsResponse: {actionSpec: LMActionSpec, dataViewSet: LMDataViewSet} = JSON.parse(request.response);
      let i = 0;
      let content = '';
      const relMenuItems: RelatedMenuItem[] = [];
      const actionSpec: LMActionSpec = relatedActionsResponse.actionSpec;
      const actionSpecString = JSON.stringify(actionSpec);
      const dataViewSet = relatedActionsResponse.dataViewSet;
      dataViewSet.data.forEach(dataView => {
        relMenuItems[ i++ ] = { text: dataView.representativeText, resourceId: dataView.resourceId };
      });
      relMenuItems.forEach(item => {
        // Build Action Reference
        const actionReference: LMBusActionReference = {
          id:         actionSpec.name + '_popup' + (i++), type: 'related',
          index:      actionSpec.index, label: actionSpec.label, requestAction: actionSpec.requestAction,
          resourceId: item.resourceId
        };
        const actionReferenceString = JSON.stringify(actionReference);
        content += this.buildToolbarMenuEntry(actionReferenceString, actionSpecString, item.text);
      });
      response(content);
    }
  };

  private buildToolbarMenuEntry(actionReferenceString: string, actionSpecString: string, title: string, hasActionForm = false): string {
    return `
      <li>
        <a href="#"
           data-action-reference=\'${actionReferenceString}\'
           data-action-spec=\'${actionSpecString}\'
           data-has-action-form=\'${hasActionForm}\'
         >${title}</a>
      </li>`;
  }

  public setScannableClickEvent() {
    this.m_searchField.button.click = (event) => this.onBarCodeClick(event);
  }

  public onBarCodeClick(event) {

    if (this.lmContextService.inNativeMobileApp() && !this.lmContextService.isMobileDevice()) {
      const test = '';
      this.processBarCodeClick(test);
    } else {
      this.lmContextUtilService.showQRCodeScanner().subscribe((response: LMBarCodeData) => {
        if (response?.data) {
          this.processBarCodeClick(response?.data);
        }
      });
    }
  }

  processBarCodeClick = (scanResults) => {
    this.searchField.value = scanResults;
    this.fireSearchEvent(event);

    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        this.elementRef?.nativeElement?.querySelectorAll('input[soho-toolbar-flex-searchfield]')[0]?.focus();
      }, 2);
    });
  };

  public fireSearchEvent(event) {
    if (!this.searchField.value) {
      // if value is blank then fire clearSearchEvent event
      this.fireClearSearchEvent(event);
    } else {
      const toolbarSearchEvent: LMToolbarSearchEvent = {
        event,
        searchField: this.searchField
      };

      this.toolbarSearchEvent.emit(toolbarSearchEvent);
    }
  }

  public fireClearSearchEvent(event) {
    const toolbarSearchEvent: LMToolbarSearchEvent = {
      event,
      searchField: this.searchField
    };
    this.toolbarSearchClearedEvent.emit(toolbarSearchEvent);
    if (this.searchField?.scannable) {
      this.searchField.value = '';
    }
  }

  /**
   * This is a way to force an afterViewChecked call, This allows
   * a call to updated on the soho toolbar which will rebuild it's
   * state based on the new markup.
   *
   * todo ppatton since only called from @Input()s and that already causes
   * change detection, and so the afterViewChecked should get called. so there
   * may be no need to call changeDetector.markForCheck()
   */
  private markForCheck() {
    if (this.doUpdate) {
      return;
    }
    this.doUpdate = true;

    // ... make sure the change detector kicks in, otherwise if the inputs
    // were change programmatically the component may not be eligible for
    // updating.
    this.changeDetector.markForCheck();
  }

  public clearAsOfDate(event: Event) {
    if (this.lmContextService.drillAroundWindow()) {
      // disallow dismissing as of date and archiving buttons
      return;
    }
    this.asOfDateClearedEvent.emit();
  }

  public overlayBackClick() {
    this.lmOverlayService.back();
  }

  quitArchiveView(event: Event) {
    if (this.lmContextService.drillAroundWindow()) {
      // disallow dismissing as of date and archiving buttons
      return;
    }
    this.quitArchiveViewEvent.emit();
  }

  quitProxyMode() {
    this.quitProxyModeEvent.emit();
  }

  quitDBSessionDebugMode() {
    this.quitDBSessionDebugModeEvent.emit();
  }

  quitSecSessionDebugMode() {
    this.quitSecSessionDebugModeEvent.emit();
  }

  quitListCostReportMode() {
    this.quitListCostReportModeEvent.emit();
  }

  showUploadStatus() {
    this.lmXLFileUploadService.showUploadInProgressDialog();
  }

  stringifyJson(actionReference: LMBusActionReference) {
    if (actionReference === undefined) {
      return '';
    }

    const actionReferenceClone = JSON.parse(JSON.stringify(actionReference));
    if (actionReferenceClone.label && actionReferenceClone.label.search(/'/g) > -1) {
      // html encode single quotes as they screw up when placed into a element.dataset.
      actionReferenceClone.label = actionReferenceClone.label.replace(/'/g, (substring, ...args) => '&#39;');
    }

    return JSON.stringify(actionReferenceClone);
  }

  private updateMaxTitleWidth() {
    this.m_updateMaxTitleWidth = true;
    this.changeDetector.markForCheck();
  }

  private setMaxTitleWidth() {
    if (!this.elementRef) {
      return; // safety
    }

    const toolbarElement: HTMLElement = this.elementRef.nativeElement;
    const flexToolbarElement = toolbarElement.querySelector<HTMLSpanElement>('soho-toolbar-flex');
    if (!flexToolbarElement) {
      return;
    }
    const toolbarRightPadding = parseInt(this.getComputedStyle(flexToolbarElement, 'padding-right'), 10);
    const toolbarLeftPadding = parseInt(this.getComputedStyle(flexToolbarElement, 'padding-left'), 10);

    // the spanElement contains the actual title. We need this to know how long the title is
    const spanElement = toolbarElement.querySelector<HTMLSpanElement>('.toolbar-section.title span');
    // for the title, a span element is wrapped inside of a h2. We will set max-width on this element
    // and inherit it down to the span, so we don't lose the width of the title and start shrinking it
    // before it's necessary
    const headerType = this.inHeader ? 'h1' : 'h2';
    const headerElement = toolbarElement.querySelector<HTMLHeadElement>('.toolbar-section.title ' + headerType);
    if (!spanElement || !headerElement) {
      // if neither element exists, then there is no title. No op
      return;
    }

    const headerRightPadding = parseInt(this.getComputedStyle(headerElement, 'padding-right'), 10);
    const headerLeftPadding = parseInt(this.getComputedStyle(headerElement, 'padding-left'), 10);

    let toolbarButtonSetWidth = 0;
    const toolbarButtonSetElement = toolbarElement.querySelector<HTMLElement>('.toolbar-section.buttonset');
    // Using computed width on the buttonset to get what actual browser space is being taken up. OffsetWidth was
    // returning how much space would be taken up if there was browser space  for it, which was incorrect for
    // determining if all the buttons were collapsed
    let buttonSetLeftPadding = 0;
    let buttonSetRightPadding = 0;
    if (toolbarButtonSetElement) {
      buttonSetLeftPadding = parseInt(this.getComputedStyle(toolbarButtonSetElement, 'padding-left'), 10);
      buttonSetRightPadding = parseInt(this.getComputedStyle(toolbarButtonSetElement, 'padding-right'), 10);
      const realWidth = parseInt(this.getComputedStyle(toolbarButtonSetElement, 'width'), 10);
      toolbarButtonSetWidth = realWidth - buttonSetLeftPadding - buttonSetRightPadding;
    }

    // Get width of the search field if it exists.
    let searchFieldWidth = 0;
    const searchField = toolbarElement.querySelector<HTMLSpanElement>('.toolbar-section.search span');
    if (searchField) {
      searchFieldWidth =parseInt(this.getComputedStyle(searchField, 'width'), 10);
    }

    // Don't set the max-width on the title until the buttonset element has a width of 0 and no search field exists.
    // If it is greater than 0, then the buttons haven't completely collapsed in to the overflow button menu yet.
    // Since we favor the title over the buttons we don't want to shrink the title until the button set is entirely collapsed
    if (!searchField && ((this.isMingleInbox && toolbarButtonSetWidth > 35) || (!this.isMingleInbox && toolbarButtonSetWidth > 0))) {
      headerElement.style.maxWidth = '';
      return;
    }

    const appMenuButton = toolbarElement.querySelector<HTMLSpanElement>('#app-menu-trigger');
    let appMenuButtonWidth = 0;
    if (appMenuButton) {
      appMenuButtonWidth = parseInt(this.getComputedStyle(appMenuButton, 'width'), 10);
    }

    const moreButtonDiv = toolbarElement.querySelector<HTMLSpanElement>('.toolbar-section.more');

    const isRTL = Soho.Locale.currentLanguage.direction === 'right-to-left';

    const toolbarWidth = parseInt(this.getComputedStyle(toolbarElement, 'width'), 10);
    // calculate any paddings on the toolbar elements
    const leftWidth = isRTL ? (moreButtonDiv ? moreButtonDiv.offsetWidth : 0) + toolbarLeftPadding + headerLeftPadding : toolbarLeftPadding + headerLeftPadding;
    const rightWidth = !isRTL ? (moreButtonDiv ? moreButtonDiv.offsetWidth : 0) + toolbarRightPadding + headerRightPadding : toolbarLeftPadding + headerRightPadding;

    /**
     * A toolbar contains:
     *
     * Toolbar:
     * AppMenuTrigger|Title|SearchField|ButtonSet|More
     *
     * only set maxWidth on the title if
     * toolbar - AppMenuTrigger - searchfield - buttonset - more < title
     */
    const spaceLeft = toolbarWidth - appMenuButtonWidth - searchFieldWidth - (toolbarButtonSetWidth + buttonSetRightPadding + buttonSetLeftPadding) - rightWidth - leftWidth;

    const currentMax = parseInt(this.getComputedStyle(headerElement, 'max-width'), 10);

    if (isNaN(currentMax)) {
      headerElement.style.maxWidth = spaceLeft + 'px';
      return;
    }

    if (spaceLeft === currentMax) {
      return;
    }

    if (spaceLeft < spanElement.offsetWidth) {
      headerElement.style.maxWidth = spaceLeft + 'px';
    } else {
      headerElement.style.maxWidth = '';
    }

  }

  private getComputedStyle(element: Element, style: string): any {
    return window.getComputedStyle(element, null).getPropertyValue(style);
  }

  // public menuButtonId(id: string): string {
  //   return id + (this.testVersion >= AppConstants.TESTVERSION_V2 ? '_toolbar_menubutton' : '');
  // }

  public elementId(id: string): string {
    return id + (this.testVersion >= AppConstants.TESTVERSION_V2 ? '_toolbar_button' : '');
  }

  public menuIdV1(id: string): string {
    return this.testVersion === AppConstants.TESTVERSION_V1 ? id : '';
  }

  public menuIdV2(id: string): string {
    return this.testVersion >= AppConstants.TESTVERSION_V2 ? id + '_toolbar_menuitem' : '';
  }

  /**
   * Determines if the toolbar buttons contain meaningful submenu items.
   *
   * This function specifically checks for two standard menu types:
   * 1. Options Menu (StandardOptionsMenuButton)
   * 2. Drill Around Menu (StandardDrillAroundMenuButton)
   *
   * The function considers submenus as active/valid when:
   * - More than 2 buttons exist
   * - Non-standard button types are present
   * - Options menu contains any items
   * - Drill around menu has multiple items
   * - Drill around menu has a single non-noop action
   *
   * @param buttons - Array of toolbar buttons to evaluate
   * @returns {boolean} true if active submenus exist, false otherwise
   *
   * @example
   * // Returns false for empty buttons
   * hasActiveSubmenus([])
   *
   * // Returns true if more than 2 buttons
   * hasActiveSubmenus([button1, button2, button3])
   *
   * // Returns false if only standard buttons with empty/noop menus
   * hasActiveSubmenus([optionsButton, drillAroundButton])
   */
  private hasActiveSubmenus(buttons: LMToolbarButton[]): boolean {
    if (!buttons?.length || buttons.length > 2)
      return buttons?.length > 2;

    const [options, drill] = [
      buttons.find(btn => btn.id?.includes(AppConstants.STANDARD_TOOLBAR_OPTIONSMENU_BUTTON)),
      buttons.find(btn => btn.id?.includes(AppConstants.STANDARD_TOOLBAR_RELATEDMENU_BUTTON))
    ];

    return !options?.id || !drill?.id ||                    // Missing standard buttons
           options.menu?.length > 0 ||                      // Options has submenus
           drill.menu?.length > 1 ||                        // Multiple drill items
           drill.menu?.[0]?.actionReference?.type !== 'noop'; // Not a noop action
  }

  private resolveButtonTooltip(button: LMToolbarButton): void {
    const info = button.customToolbarButtonInfo;
    if (info) {
      info.resolvedTooltip = !info.text && info.tooltip !== info.text ? (info.tooltip || '') : '';
      info.menu?.forEach(menuItem => this.resolveButtonTooltip(menuItem));
    } else if (button.actionReference) {
      const ref = button.actionReference;
      button.resolvedTooltip = button.isIconOnly && ref.tooltip !== ref.label ? (ref.tooltip || ref.label || '') : '';
    }
  }

  public getActionDescription(action: LMToolbarButton): void {
    if (!action?.data) {
      return;
    }
    let message: string | null = null;

    switch (action.data) {
      case 'Sort':
        message = Soho.Locale.translate(
          action.icon === 'sort-down'
            ? 'SortActionDescriptionDesc'
            : 'SortActionDescriptionAsc'
        );
        break;
      // using switch case to handle future actions
      default:
        break;
    }
    if (!message) {
      return;
    }
    this.liveMessageSortAction = {
      liveMessage: message,
      ariaLive: 'polite',
      ariaAtomic: true,
      useHostAsLiveRegion: true
    };
  }

 updateReportAsOfDate() {
   this.updateReportAsOfDateEvent.emit();
 }

 private setFavorTitle(): void {
    if (this.inHeader) {
      // in header
      if (this.showCalledOutMenuButton) {
        // in the case of called out actions only, we want to make sure the actions button isn't shrunk
        this.favorTitle = false;
      } else if (this.hideOverflowMenuButton) {
        this.favorTitle = false;
      }
    } else {
      if (this.isInOverlay) {
        // favor buttons if in the overlay
        this.favorTitle = false;
      } else if (this.showCalledOutMenuButton) {
        this.favorTitle = false;
      }
    }
 }
}
